// Touch controls (ui-flow §11.3, ui-qol §12, movement.md §2.2, §4.3): a floating joystick or
// follow-finger steering, ✂ split and ● shoot buttons, ☰ menu, 🎥 camera while spectating, pinch
// zoom. The money-room $ hold-to-cash-out button belongs to the money HUD (ui/hud/money.ts,
// with its progress ring); the copy that used to live here was never shown and is gone.
//
// - Steering: the first touch on the canvas that is not on a button steers. The joystick base
//   appears where that finger lands (floating). `finger` is its position (CSS px,
//   canvas-relative); `stick` is the joystick vector from where it went down, length clamped to
//   1 at 60 px. Both are raw: input/steer.ts applies the 10 % dead zone, the proportional speed
//   and the release rules, every frame. `steer` true/false marks a steering touch starting /
//   ending; `steerSeq` counts steering touch-downs.
// - Release memory: at the lift, `releaseStick` = the strongest vector of the last 50 ms (a knob
//   springing back toward the centre as the finger lifts is ignored) and `releaseFinger` = the
//   last finger position, for "keep moving" (Stop when finger lifts off). Losing the touch
//   another way (blur, controls hidden) clears both: the cell stops.
// - Buttons: a tap is one action, sent on touch-down; holding ● emits `ejectHeld` (repeats only
//   when "Hold W" is on, which the input sender decides). Button touches never steer.
// - Two canvas touches pinch: `pinch { factor }` per move (multiply the wheel zoom by it).
// - Shown when `(pointer: coarse)` matches or after the first touchstart, and only while
//   `setActive(true)` (playing or spectating). Split/shoot hide while spectating; 🎥 shows then.
// - Look (ui-qol §12): split/shoot are 64 px circles rgba(0,0,0,.35) with white 28 px glyphs,
//   bottom-right with 16 px gaps; joystick base a 120 px ring rgba(255,255,255,.25), knob 48 px
//   rgba(255,255,255,.5); ☰ 40 px at the top-left. Elements carry `touch-*` classes.
//
// Implementer: client input/UI (M2). Consumers: app.ts, input/steer.ts (TouchSteerSource).

import { Emitter } from '../net/socket.js';
import type { TouchSteerMode } from '../settings/store.js';
import type { TouchSteerSource } from './steer.js';

export interface TouchEvents {
  action: 'split' | 'eject' | 'special' | 'escape';
  ejectHeld: boolean;
  /** Continuous pinch zoom: multiply wheelZoom by this factor. */
  pinch: { readonly factor: number };
  /** A steering touch went down (true) or lifted (false). */
  steer: boolean;
}

/** Joystick radius: |stick| = 1 at this drag, in CSS px (ui-flow §11.3). */
export const JOYSTICK_RADIUS_PX = 60;
/** The strongest stick vector of this window before a lift is kept (movement.md §4.3). */
export const RELEASE_WINDOW_MS = 50;
const JOYSTICK_RING_PX = 120;
const JOYSTICK_KNOB_PX = 48;
const ACTION_BUTTON_PX = 64;
const MENU_BUTTON_PX = 40;
const EDGE_PX = 16;
const GAP_PX = 16;
/** Stick samples kept for the release window (touchmove runs at ≤ 120 Hz: 6 in 50 ms). */
const RELEASE_SAMPLES = 16;

/** The joystick vector for a drag of (dx, dy) CSS px: its length is clamped to 1 at 60 px. */
export function joystickVector(
  dx: number,
  dy: number,
  out: { x: number; y: number } = { x: 0, y: 0 },
): { x: number; y: number } {
  const len = Math.hypot(dx, dy);
  const k = len > JOYSTICK_RADIUS_PX ? 1 / len : 1 / JOYSTICK_RADIUS_PX;
  out.x = dx * k;
  out.y = dy * k;
  return out;
}

interface TouchPoint {
  x: number;
  y: number;
}

/**
 * The vector to keep after a lift at `endMs`: the longest of the stick values in effect during
 * `[endMs − windowMs, endMs]` (samples `t` ascending, each in effect until the next one). Null
 * without samples.
 */
export function releaseVector(
  samples: readonly { readonly t: number; readonly x: number; readonly y: number }[],
  endMs: number,
  windowMs: number = RELEASE_WINDOW_MS,
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestLen = -1;
  const from = endMs - windowMs;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i] as { t: number; x: number; y: number };
    const next = samples[i + 1];
    // In effect at some point of the window: it started before the end and ended after `from`.
    if (s.t > endMs || (next !== undefined && next.t < from)) continue;
    const len = Math.hypot(s.x, s.y);
    if (len > bestLen) {
      bestLen = len;
      best = { x: s.x, y: s.y };
    }
  }
  return best;
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export class TouchInput implements TouchSteerSource {
  readonly events = new Emitter<TouchEvents>();
  /** Steering vector from the joystick, length ≤ 1 (raw); null when not steering. */
  stick: { x: number; y: number } | null = null;
  /** Finger position in CSS px; null when no steering finger is down. */
  finger: { x: number; y: number } | null = null;
  /** The stick vector kept from the last lift (see the header); null = stop. */
  releaseStick: { x: number; y: number } | null = null;
  /** The finger position at the last lift; null = stop. */
  releaseFinger: { x: number; y: number } | null = null;
  /** Steering touch-downs so far. */
  steerSeq = 0;
  /** Steering style (`settings.touchSteer`); the joystick ring is drawn only for 'joystick'. */
  mode: TouchSteerMode = 'joystick';
  /** Touch is in use on this device (coarse pointer, or a touch happened). */
  enabled = false;
  protected readonly canvas: HTMLCanvasElement;
  protected readonly overlay: HTMLElement;
  private readonly now: () => number;

  private attached = false;
  private active = false;
  private spectating = false;
  /** Recent stick values (time ascending) of the current steering touch. */
  private readonly samples: { t: number; x: number; y: number }[] = [];
  /** Canvas touches currently down (button touches are not tracked here). */
  private readonly touches = new Map<number, TouchPoint>();
  private steerId: number | null = null;
  private originX = 0;
  private originY = 0;
  private pinchDist = 0;
  private ejectTouches = 0;

  private root: HTMLElement | null = null;
  private ring: HTMLElement | null = null;
  private knob: HTMLElement | null = null;
  private splitBtn: HTMLElement | null = null;
  private shootBtn: HTMLElement | null = null;
  private menuBtn: HTMLElement | null = null;
  private cameraBtn: HTMLElement | null = null;
  private readonly offs: (() => void)[] = [];

  constructor(canvas: HTMLCanvasElement, overlay: HTMLElement, now: () => number = defaultNow) {
    this.canvas = canvas;
    this.overlay = overlay;
    this.now = now;
  }

  attach(): void {
    if (this.attached) return;
    const doc = this.canvas.ownerDocument;
    const win = doc?.defaultView ?? null;
    if (!doc || !win) return;
    this.attached = true;
    try {
      if (win.matchMedia?.('(pointer: coarse)').matches) this.enabled = true;
    } catch {
      // no matchMedia: wait for a touch
    }
    this.build(doc);
    const opts: AddEventListenerOptions = { passive: false };
    this.listen(this.canvas, 'touchstart', (ev) => this.touchStart(ev as TouchEvent), opts);
    this.listen(this.canvas, 'touchmove', (ev) => this.touchMove(ev as TouchEvent), opts);
    this.listen(this.canvas, 'touchend', (ev) => this.touchEnd(ev as TouchEvent), opts);
    this.listen(this.canvas, 'touchcancel', (ev) => this.touchEnd(ev as TouchEvent), opts);
    this.listen(win, 'blur', () => this.releaseAll());
    this.refresh();
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    for (const off of this.offs) off();
    this.offs.length = 0;
    this.releaseAll();
    this.root?.remove();
    this.root = null;
    this.ring = null;
    this.knob = null;
    this.splitBtn = null;
    this.shootBtn = null;
    this.menuBtn = null;
    this.cameraBtn = null;
  }

  /** Show the controls (playing or spectating) or hide them (menus). */
  setActive(active: boolean): void {
    this.active = active;
    if (!active) this.releaseAll();
    this.refresh();
  }

  /** Spectating shows 🎥 (= Q) instead of split and shoot. */
  setSpectating(spectating: boolean): void {
    this.spectating = spectating;
    this.refresh();
  }

  // -------------------------------------------------------------------------------------------

  private listen(
    target: EventTarget,
    type: string,
    fn: (ev: Event) => void,
    opts?: AddEventListenerOptions,
  ): void {
    target.addEventListener(type, fn, opts);
    this.offs.push(() => target.removeEventListener(type, fn, opts));
  }

  private build(doc: Document): void {
    const root = doc.createElement('div');
    root.className = 'touch-controls';
    root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:5;';
    const ring = doc.createElement('div');
    ring.className = 'touch-joystick';
    ring.style.cssText =
      `position:absolute;width:${JOYSTICK_RING_PX}px;height:${JOYSTICK_RING_PX}px;` +
      `margin:-${JOYSTICK_RING_PX / 2}px 0 0 -${JOYSTICK_RING_PX / 2}px;border-radius:50%;` +
      'box-sizing:border-box;border:3px solid rgba(255,255,255,0.25);display:none;';
    const knob = doc.createElement('div');
    knob.className = 'touch-joystick-knob';
    knob.style.cssText =
      `position:absolute;left:50%;top:50%;width:${JOYSTICK_KNOB_PX}px;` +
      `height:${JOYSTICK_KNOB_PX}px;margin:-${JOYSTICK_KNOB_PX / 2}px 0 0 ` +
      `-${JOYSTICK_KNOB_PX / 2}px;border-radius:50%;background:rgba(255,255,255,0.5);`;
    ring.append(knob);
    root.append(ring);
    const corner = (bottom: number, right: number): string =>
      `bottom:calc(${bottom}px + env(safe-area-inset-bottom));` +
      `right:calc(${right}px + env(safe-area-inset-right));`;
    const step = ACTION_BUTTON_PX + GAP_PX;
    // Split in the corner, shoot to its left (16 px gaps).
    this.splitBtn = this.button(
      doc,
      root,
      'touch-split',
      'Split',
      '✂',
      ACTION_BUTTON_PX,
      corner(EDGE_PX, EDGE_PX),
    );
    this.shootBtn = this.button(
      doc,
      root,
      'touch-shoot',
      'Shoot mass',
      '●',
      ACTION_BUTTON_PX,
      corner(EDGE_PX, EDGE_PX + step),
    );
    this.menuBtn = this.button(
      doc,
      root,
      'touch-menu',
      'Menu',
      '☰',
      MENU_BUTTON_PX,
      `top:calc(${EDGE_PX}px + env(safe-area-inset-top));` +
        `left:calc(${EDGE_PX}px + env(safe-area-inset-left));`,
    );
    this.cameraBtn = this.button(
      doc,
      root,
      'touch-camera',
      'Switch camera',
      '🎥',
      ACTION_BUTTON_PX,
      corner(EDGE_PX, EDGE_PX),
    );
    this.overlay.append(root);
    this.root = root;
    this.ring = ring;
    this.knob = knob;

    this.press(this.splitBtn, () => this.events.emit('action', 'split'));
    this.press(this.menuBtn, () => this.events.emit('action', 'escape'));
    this.press(this.cameraBtn, () => this.events.emit('action', 'special'));
    this.press(
      this.shootBtn,
      () => {
        this.events.emit('action', 'eject');
        if (this.ejectTouches++ === 0) this.events.emit('ejectHeld', true);
      },
      () => {
        if (this.ejectTouches > 0 && --this.ejectTouches === 0)
          this.events.emit('ejectHeld', false);
      },
    );
  }

  private button(
    doc: Document,
    root: HTMLElement,
    cls: string,
    label: string,
    glyph: string,
    size: number,
    place: string,
  ): HTMLElement {
    const b = doc.createElement('button');
    b.type = 'button';
    b.className = `touch-btn ${cls}`;
    b.setAttribute('aria-label', label);
    b.textContent = glyph;
    b.style.cssText =
      `position:absolute;${place}width:${size}px;height:${size}px;border-radius:50%;` +
      'border:0;padding:0;background:rgba(0,0,0,0.35);color:#fff;line-height:1;' +
      `font-size:${size >= ACTION_BUTTON_PX ? 28 : 20}px;pointer-events:auto;touch-action:none;` +
      'user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent;';
    root.append(b);
    return b;
  }

  /** Down → `onDown` (once per touch); up/cancel → `onUp`. Never steers, never focuses. */
  private press(el: HTMLElement, onDown: () => void, onUp?: () => void): void {
    const down = (ev: Event): void => {
      if (ev.cancelable) ev.preventDefault();
      this.enable();
      onDown();
    };
    const up = (ev: Event): void => {
      if (ev.cancelable) ev.preventDefault();
      onUp?.();
    };
    this.listen(el, 'touchstart', down, { passive: false });
    this.listen(el, 'touchend', up, { passive: false });
    this.listen(el, 'touchcancel', up, { passive: false });
  }

  private enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    this.refresh();
  }

  private refresh(): void {
    const visible = this.enabled && this.active;
    const root = this.root;
    if (root) root.style.display = visible ? '' : 'none';
    const playing = visible && !this.spectating;
    if (this.splitBtn) this.splitBtn.style.display = playing ? '' : 'none';
    if (this.shootBtn) this.shootBtn.style.display = playing ? '' : 'none';
    if (this.cameraBtn) this.cameraBtn.style.display = visible && this.spectating ? '' : 'none';
    if (!visible) this.showRing(false);
  }

  private point(t: Touch): TouchPoint {
    const rect = this.canvas.getBoundingClientRect();
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }

  private touchStart(ev: TouchEvent): void {
    if (ev.cancelable) ev.preventDefault();
    this.enable();
    if (!this.active) return;
    for (const t of Array.from(ev.changedTouches)) {
      const p = this.point(t);
      this.touches.set(t.identifier, p);
      if (this.steerId === null) {
        this.steerId = t.identifier;
        this.steerSeq++;
        this.originX = p.x;
        this.originY = p.y;
        this.finger = { x: p.x, y: p.y };
        this.stick = { x: 0, y: 0 };
        this.releaseStick = null;
        this.releaseFinger = null;
        this.samples.length = 0;
        this.sample(0, 0);
        this.placeRing(p.x, p.y);
        this.events.emit('steer', true);
      }
    }
    if (this.touches.size >= 2) this.pinchDist = this.spread();
  }

  private touchMove(ev: TouchEvent): void {
    if (ev.cancelable) ev.preventDefault();
    for (const t of Array.from(ev.changedTouches)) {
      const p = this.touches.get(t.identifier);
      if (!p) continue;
      const q = this.point(t);
      p.x = q.x;
      p.y = q.y;
      if (t.identifier === this.steerId) {
        this.finger = { x: q.x, y: q.y };
        const v = joystickVector(q.x - this.originX, q.y - this.originY, this.stick ?? undefined);
        this.stick = v;
        this.sample(v.x, v.y);
        this.moveKnob(v.x, v.y);
      }
    }
    if (this.touches.size >= 2) {
      const d = this.spread();
      if (this.pinchDist > 0 && d > 0) {
        const factor = d / this.pinchDist;
        if (factor !== 1 && Number.isFinite(factor)) this.events.emit('pinch', { factor });
      }
      this.pinchDist = d;
    }
  }

  private touchEnd(ev: TouchEvent): void {
    if (ev.cancelable) ev.preventDefault();
    for (const t of Array.from(ev.changedTouches)) {
      this.touches.delete(t.identifier);
      if (t.identifier === this.steerId) this.endSteer(ev.type !== 'touchcancel');
    }
    this.pinchDist = this.touches.size >= 2 ? this.spread() : 0;
  }

  /** Distance between the first two tracked canvas touches. */
  private spread(): number {
    let a: TouchPoint | null = null;
    for (const p of this.touches.values()) {
      if (!a) a = p;
      else return Math.hypot(p.x - a.x, p.y - a.y);
    }
    return 0;
  }

  /** Records the stick value now in effect (for the release window). */
  private sample(x: number, y: number): void {
    const list = this.samples;
    if (list.length >= RELEASE_SAMPLES) list.shift();
    list.push({ t: this.now(), x, y });
  }

  /** The steering touch ended: `keep` (a normal lift) records the release memory. */
  private endSteer(keep: boolean): void {
    if (this.steerId === null) {
      if (!keep) {
        this.releaseStick = null;
        this.releaseFinger = null;
      }
      return;
    }
    this.steerId = null;
    if (keep) {
      this.releaseStick = releaseVector(this.samples, this.now());
      this.releaseFinger = this.finger ? { x: this.finger.x, y: this.finger.y } : null;
    } else {
      this.releaseStick = null;
      this.releaseFinger = null;
    }
    this.samples.length = 0;
    this.stick = null;
    this.finger = null;
    this.showRing(false);
    this.events.emit('steer', false);
  }

  private releaseAll(): void {
    this.touches.clear();
    this.pinchDist = 0;
    this.endSteer(false);
    if (this.ejectTouches > 0) {
      this.ejectTouches = 0;
      this.events.emit('ejectHeld', false);
    }
  }

  private placeRing(x: number, y: number): void {
    if (!this.ring || this.mode !== 'joystick') return;
    this.ring.style.left = `${x}px`;
    this.ring.style.top = `${y}px`;
    this.moveKnob(0, 0);
    this.showRing(true);
  }

  private moveKnob(vx: number, vy: number): void {
    if (!this.knob) return;
    this.knob.style.transform = `translate(${vx * JOYSTICK_RADIUS_PX}px, ${vy * JOYSTICK_RADIUS_PX}px)`;
  }

  private showRing(on: boolean): void {
    if (this.ring) this.ring.style.display = on ? '' : 'none';
  }
}
